import { io, Socket } from 'socket.io-client';
import { Email } from '../types';

class WebSocketService {
  private socket: Socket | null = null;
  private currentMailboxId: string | null = null;
  private currentAccessToken: string | undefined;
  private newEmailHandler: ((email: Email) => void) | null = null;
  private mailboxUpdatedHandlers: Set<(payload: any) => void> = new Set();
  private mailboxCreatedHandlers: Set<(payload: any) => void> = new Set();
  private mailboxDeletedHandlers: Set<(payload: any) => void> = new Set();
  private connectionHandlers: Set<(connected: boolean) => void> = new Set();

  private emitConnectionState(connected: boolean) {
    Array.from(this.connectionHandlers).forEach((handler) => {
      try {
        handler(connected);
      } catch (e) {
        console.error('websocket connection handler error', e);
      }
    });
  }

  private maybeAutoDisconnect() {
    const hasMailboxSubscription = Boolean(this.currentMailboxId);
    const hasGlobalHandlers =
      this.mailboxUpdatedHandlers.size > 0 ||
      this.mailboxCreatedHandlers.size > 0 ||
      this.mailboxDeletedHandlers.size > 0;
    if (!hasMailboxSubscription && !hasGlobalHandlers) {
      this.disconnect();
    }
  }

  private resubscribeIfNeeded() {
    if (!this.socket || !this.currentMailboxId) return;
    this.socket.emit('subscribe-mailbox', {
      mailboxId: this.currentMailboxId,
      accessToken: this.currentAccessToken
    });
  }

  connect() {
    if (this.socket?.connected) return;
    if (this.socket) return;

    const url = process.env.NODE_ENV === 'production' 
      ? window.location.origin 
      : (process.env.REACT_APP_WS_URL || 'http://localhost:3001');

    const token = localStorage.getItem('token');
    this.socket = io(url, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000
    });

    this.socket.on('connect', () => {
      this.emitConnectionState(true);
      this.resubscribeIfNeeded();
    });
    this.socket.on('disconnect', () => {
      this.emitConnectionState(false);
    });

    this.socket.on('connect_error', (error) => {
      this.emitConnectionState(false);
      console.error('🔌 WebSocket connection error:', error);
    });

    this.socket.on('new-email', (email: Email) => {
      if (!this.newEmailHandler) return;
      this.newEmailHandler(email);
    });

    this.socket.on('mailbox-updated', (payload: any) => {
      Array.from(this.mailboxUpdatedHandlers).forEach((handler) => {
        try {
          handler(payload);
        } catch (e) {
          console.error('mailbox-updated handler error', e);
        }
      });
    });

    this.socket.on('mailbox-created', (payload: any) => {
      Array.from(this.mailboxCreatedHandlers).forEach((handler) => {
        try {
          handler(payload);
        } catch (e) {
          console.error('mailbox-created handler error', e);
        }
      });
    });

    this.socket.on('mailbox-deleted', (payload: any) => {
      Array.from(this.mailboxDeletedHandlers).forEach((handler) => {
        try {
          handler(payload);
        } catch (e) {
          console.error('mailbox-deleted handler error', e);
        }
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.currentMailboxId = null;
      this.currentAccessToken = undefined;
      this.newEmailHandler = null;
      this.emitConnectionState(false);
    }
  }

  subscribeToMailbox(mailboxId: string, accessToken: string | undefined, onNewEmail: (email: Email) => void) {
    if (!this.socket) {
      this.connect();
    }
    this.currentMailboxId = mailboxId;
    this.currentAccessToken = accessToken;
    this.newEmailHandler = onNewEmail;
    this.resubscribeIfNeeded();
  }

  unsubscribeFromMailbox() {
    if (this.currentMailboxId && this.socket) {
      this.socket.emit('unsubscribe-mailbox', this.currentMailboxId);
    }
    this.currentMailboxId = null;
    this.currentAccessToken = undefined;
    this.newEmailHandler = null;
    this.maybeAutoDisconnect();
  }

  onMailboxUpdated(handler: (payload: any) => void) {
    this.mailboxUpdatedHandlers.add(handler);
    if (!this.socket) this.connect();
    return () => {
      this.mailboxUpdatedHandlers.delete(handler);
      this.maybeAutoDisconnect();
    };
  }

  onMailboxCreated(handler: (payload: any) => void) {
    this.mailboxCreatedHandlers.add(handler);
    if (!this.socket) this.connect();
    return () => {
      this.mailboxCreatedHandlers.delete(handler);
      this.maybeAutoDisconnect();
    };
  }

  onMailboxDeleted(handler: (payload: any) => void) {
    this.mailboxDeletedHandlers.add(handler);
    if (!this.socket) this.connect();
    return () => {
      this.mailboxDeletedHandlers.delete(handler);
      this.maybeAutoDisconnect();
    };
  }

  isConnected() {
    return Boolean(this.socket?.connected);
  }

  onConnectionState(handler: (connected: boolean) => void) {
    this.connectionHandlers.add(handler);
    handler(this.isConnected());
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }
}

// Создаем единственный экземпляр
const websocketService = new WebSocketService();

export default websocketService;
